import { useState } from 'react'
import { useLiveViewStateStore } from '../LiveViewStore'
import {
    addBlackmaskRequest,
    deleteBlackmask,
    editBlackmaskRequest,
    getBlackmask,
    getBlackmaskStatus,
    setBlackmaskStatus
} from '.././api/requests'
import { Blackmask, NormalizedPoint, Point } from '../Blackmask'

export const useBlackmaskApi = (baseURL: string | null, imageRef: React.RefObject<HTMLIFrameElement>) => {
    const [blackmasks, setBlackmasks] = useState<Blackmask[]>([{ id: 0, points: [] }])
    const { blackmaskEnabled } = useLiveViewStateStore()

    // Convert pixel coordinates to normalized coordinates (0-1)
    const pixelsToNormalized = (point: Point, canvas: HTMLCanvasElement): NormalizedPoint => {
        return {
            x: Math.min(1, point.x / canvas.width),
            y: Math.min(1, point.y / canvas.height)
        }
    }

    const fetchBlackmasks = async () => {
        if (imageRef.current) {
            const res = await getBlackmask(baseURL)
            const masks = res.data.black_masks?.map((mask: any) => {
                return {
                    id: mask.id,
                    points: mask.coords.map((coord: [number, number]) => ({
                        x: Math.min(1, coord[0]),
                        y: Math.min(1, coord[1])
                    }))
                }
            })
            setBlackmasks(masks)
            return masks
        }
    }
    const getBlackmaskEnabled = async () => {
        const res = await getBlackmaskStatus(baseURL)
        return res.data.enabled
    }
    const setBlackmaskEnabled = async (enabled: boolean) => {
        await setBlackmaskStatus(baseURL, enabled)
    }

    const addNewBlackmask = async (points: Point[], canvas: HTMLCanvasElement) => {
        const normalizedPoints = points.map((point) => pixelsToNormalized(point, canvas))
        const coords = normalizedPoints.map((point) => [Math.min(1, point.x), Math.min(1, point.y)])
        try {
            await addBlackmaskRequest(baseURL, { coords })
            await fetchBlackmasks()
        } catch (err) {
            console.error('Failed to add blackmask:', err)
        }
    }

    const editBlackmask = async (id: number, points: Point[], canvas: HTMLCanvasElement) => {
        const normalizedPoints = points.map((point) => pixelsToNormalized(point, canvas))
        const coords = normalizedPoints.map((point) => [Math.min(1, point.x), Math.min(1, point.y)])
        try {
            await editBlackmaskRequest(baseURL, id, { coords })
            await fetchBlackmasks()
        } catch (err) {
            console.error('Failed to edit blackmask:', err)
        }
    }

    const deleteBlackmaskById = async (id: number) => {
        try {
            await deleteBlackmask(baseURL, id)
            await fetchBlackmasks()
        } catch (err) {
            console.error('Failed to delete blackmask:', err)
        }
    }

    return {
        blackmasks,
        setBlackmasks,
        fetchBlackmasks,
        addNewBlackmask,
        editBlackmask,
        deleteBlackmaskById,
        getBlackmaskEnabled,
        setBlackmaskEnabled
    }
}
